<template>
  <!-- The category grid is `flex flex-wrap`, so these already stretch to the tallest card in the
       row; `flex` here and `flex-1` down the chain is what actually spends that height, letting a
       short card grow and its footer row settle at the bottom instead of floating mid-card. -->
  <article class="flex" :class="cardLayoutClasses" :data-product-id="productId">
    <section
      class="relative flex flex-col flex-1 w-full overflow-hidden transition-all bg-white border rounded-lg"
      :class="[
        showsCartTreatment ? productCardInCartShadowClasses : cardShadowClasses,
        highlighted && 'ring-2 ring-brand-500 ring-offset-2 animate-pulse'
      ]"
    >
      <!-- The whole card is the click target: BOT-1108 moves every customization control into the
           product modal, so there is nothing else on the card to click. -->
      <div
        role="button"
        :tabindex="interactive ? 0 : -1"
        :aria-disabled="interactive ? 'false' : 'true'"
        :aria-label="cardLabel"
        :data-testid="testIdPrefix"
        class="flex flex-col flex-1 text-left"
        :class="interactive ? 'cursor-pointer' : 'cursor-default'"
        @click="open"
        @keydown.enter.prevent="open"
        @keydown.space.prevent="open"
      >
        <!-- `flex-1` on the body, not the footer: the extra height goes to the copy, and the price
             row stays its natural size pinned to the bottom edge. -->
        <div class="flex flex-1 gap-3 px-3 pt-3 pb-2">
          <div class="flex-grow min-w-0">
            <div v-if="chips.length" class="flex flex-wrap gap-1 mb-2">
              <Chip v-for="chip in chips" :key="chip.text" :label="chip.text" />
            </div>

            <h2 class="pb-2 pr-3 text-lg font-bold leading-5 tracking-tighter">
              {{ name }}
            </h2>

            <p
              v-if="description"
              :data-testid="`${testIdPrefix}-description`"
              class="collapsed pr-3 text-sm text-gray-brand-700"
            >
              {{ description }}
            </p>

            <!-- What the customer chose, not what the merchant wrote. Two lines of the same product
                 are otherwise identical on the card, so this is the only thing telling them apart
                 without opening each one. -->
            <p
              v-if="selections.length"
              :data-testid="`${testIdPrefix}-selections`"
              class="pt-1 pr-3 text-sm font-medium text-gray-brand-900"
            >
              {{ selections.join(', ') }}
            </p>

            <ProductNutritionalInformation
              v-if="product && product.nutrition"
              :nutritional-information="product.nutrition"
              :data-testid="`${testIdPrefix}-nutrition`"
              flush
              class="pt-1 pr-3"
            />
          </div>

          <img
            v-if="product && product.thumbUrl"
            :src="product.thumbUrl"
            :alt="name"
            loading="lazy"
            :data-testid="`${testIdPrefix}-thumb`"
            class="flex-none object-cover w-20 h-20 rounded-lg"
          />
        </div>

        <!-- The price and the add control sit in their own ruled-off row, the way V1 has always
             presented them - the rule is what separates "what this is" from "what it costs". -->
        <div class="flex items-center justify-between px-3 py-3 border-t border-gray-brand-300">
          <div
            :data-testid="`${testIdPrefix}-price`"
            class="my-auto text-sm font-bold text-gray-brand-900 tabular-nums"
          >
            <s
              v-if="strikePriceLabel"
              :data-testid="`${testIdPrefix}-strike-price`"
              class="mr-1 font-normal text-gray-brand-600"
            >
              {{ strikePriceLabel }}
            </s>
            <!-- The active price gets its own element rather than sitting as a bare text node, so
                 that "was $6.00, now $3.00" can be read as two prices instead of one string. It is
                 absent, not empty, when there is no price - a store with show_prices off should
                 leave nothing behind at all. -->
            <span v-if="priceLabel" :data-testid="`${testIdPrefix}-active-price`">{{ priceLabel }}</span>
          </div>

          <!-- An unavailable line has nothing left to configure - the product is off the menu for
               the chosen fulfillment - so the only action it offers is getting rid of it. -->
          <button
            v-if="unavailable"
            type="button"
            :data-testid="`${testIdPrefix}-remove`"
            :aria-label="`Remove ${name}`"
            class="inline-flex items-center h-8 px-3 text-sm font-bold text-white rounded-lg bg-gray-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-800"
            @click.stop="remove"
          >
            {{ removeLabel }}
          </button>
          <!-- The plus emits `increase` and the minus `decrease`; the caller decides what each does to
               the cart. A tap, Enter or Space on the stepper must not also reach the card. -->
          <div
            v-else-if="showsCartStepper"
            role="group"
            :aria-label="`${cartQuantity} ${name} in cart`"
            :data-testid="`${testIdPrefix}-stepper`"
            class="inline-flex items-center h-8 overflow-hidden font-bold text-white rounded-lg shadow-sm bg-brand-500"
            @click.stop
            @keydown.enter.stop
            @keydown.space.stop
          >
            <button
              type="button"
              :data-testid="`${testIdPrefix}-decrease`"
              :aria-label="`Remove one ${name}`"
              :disabled="decreaseDisabled"
              class="flex items-center justify-center w-8 h-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
              @click="decrease"
            >
              <MinusIcon class="w-4 h-4" />
            </button>
            <span :data-testid="`${testIdPrefix}-quantity`" class="min-w-[1.25rem] text-sm text-center tabular-nums">
              {{ cartQuantity }}
            </span>
            <button
              type="button"
              :data-testid="`${testIdPrefix}-increase`"
              :aria-label="`Add another ${name}`"
              :disabled="soldOut || increaseDisabled"
              class="flex items-center justify-center w-8 h-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
              @click="increase"
            >
              <PlusIcon class="w-4 h-4" />
            </button>
          </div>
          <button
            v-else-if="soldOut"
            type="button"
            disabled
            :data-testid="`${testIdPrefix}-sold-out`"
            class="inline-block w-24 h-8 text-xs font-medium tracking-tight rounded-lg bg-gray-brand-300 text-gray-brand-700"
          >
            Sold Out
          </button>
          <button
            v-else
            type="button"
            :data-testid="`${testIdPrefix}-add`"
            :aria-label="openLabel"
            class="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg bg-brand-500 px-2 font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-800"
            @click.stop="add"
          >
            <span v-if="inCart" :data-testid="`${testIdPrefix}-quantity`" class="text-sm tabular-nums">
              {{ quantity }}
            </span>
            <PlusIcon v-else class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- V1 hangs its per-line fulfillment-date picker off the bottom of the card. Nothing in V2
           fills this, which is why it lives in a slot rather than in the shared body. -->
      <slot name="below" />
    </section>
  </article>
</template>

<script>
import { MinusIcon, PlusIcon } from '@heroicons/vue/solid'
import Chip from '@/components/Molecules/Chip.vue'
import ProductNutritionalInformation from '@/components/Product/ProductNutritionalInformation.vue'
import { cardShadowClasses, productCardInCartShadowClasses } from '@/config'
import { formatPrice } from '@/composables/useCartMutation'
import { fromPriceCents } from '@/models/productViewModel'
import { useProductCardLayout } from '@/composables/useProductCardLayout'

export default {
  name: 'ProductCard',

  components: { Chip, MinusIcon, PlusIcon, ProductNutritionalInformation },

  props: {
    // A normalized product from @/models/productViewModel (fromV1 or fromV2). This component never
    // learns which checkout version produced it, which is what keeps the two cards identical.
    product: { type: Object, default: null },
    currency: { type: String, default: 'USD' },
    quantity: { type: Number, default: 0 },
    // Everything this product holds in the cart, across every line. A grid card stands for the
    // product rather than a line, so it carries no quantity of its own but still needs to look
    // like something you have already ordered. Defaults to the line's own quantity.
    cartQuantity: { type: Number, default: null },
    // The line's chosen modifiers, already labelled - see selectionSummary. Empty on a menu card,
    // which stands for the product and has chosen nothing.
    selections: { type: Array, default: () => [] },
    soldOut: { type: Boolean, default: false },
    // A line for a product that is no longer available for the chosen fulfillment. It still counts
    // in the cart, so it has to stay removable even though it can no longer be configured.
    unavailable: { type: Boolean, default: false },
    highlighted: { type: Boolean, default: false },
    // What one of this card's cart line costs, in cents - the line's configuration priced per unit.
    // Null on a card with no line behind it, which is what makes the price fall back to "from $X".
    linePriceCents: { type: Number, default: null },
    // The same unit before its discounts. Struck through when it is genuinely higher.
    lineOriginalPriceCents: { type: Number, default: null },
    // What the product itself would cost without the discount it is carrying - an upsell price, or
    // a membership/tier price below the default. Used on a card with no line, where there is no
    // line price to compare against.
    basePriceOriginalCents: { type: Number, default: null },
    productId: { type: [String, Number], default: null },
    // V1 and V2 carry different data-testids into their existing specs and the e2e suite, so the
    // prefix is injected rather than hardcoded.
    testIdPrefix: { type: String, default: 'product-card' },
    // BOT-1730: once the product is in the cart, swap the + for a stepper showing `cartQuantity`.
    // The + and the stepper's plus emit `increase`, its minus `decrease`, so only a caller that can
    // change the cart asks for it.
    cartStepper: { type: Boolean, default: false },
    // The stepper's minus, while the cart cannot be changed (the store is not taking orders).
    decreaseDisabled: { type: Boolean, default: false },
    // The stepper's plus, when the caller has no room for more.
    increaseDisabled: { type: Boolean, default: false }
  },

  emits: ['open', 'remove', 'decrease', 'increase'],

  setup() {
    const { cardLayoutClasses } = useProductCardLayout()
    return { cardShadowClasses, productCardInCartShadowClasses, cardLayoutClasses }
  },

  computed: {
    name() {
      return (this.product && this.product.name) || ''
    },

    description() {
      return (this.product && this.product.description) || ''
    },

    chips() {
      return (this.product && this.product.chips) || []
    },

    inCart() {
      return this.quantity > 0
    },

    // Styling only. `inCart` stays line-scoped so the quantity badge, the Edit label and the line
    // price all keep describing a line the card actually has.
    showsCartTreatment() {
      return (this.cartQuantity != null ? this.cartQuantity : this.quantity) > 0
    },

    showsCartStepper() {
      return this.cartStepper && (this.cartQuantity || 0) > 0
    },

    interactive() {
      return !this.soldOut && !this.unavailable
    },

    openLabel() {
      return `${this.inCart ? 'Edit' : 'Add'} ${this.name}`
    },

    cardLabel() {
      return this.unavailable ? `${this.name} - no longer available` : this.openLabel
    },

    removeLabel() {
      if (this.quantity < 1) return 'Remove'
      return `Remove ${this.quantity} item${this.quantity === 1 ? '' : 's'}`
    },

    formattedBasePrice() {
      return formatPrice(this.product.basePriceCents, this.currency)
    },

    // A card with a line behind it prices THAT LINE'S configuration rather than the product:
    // "from $23.00" answers "what would one cost", and a customer looking down their cart is asking
    // "what does this one cost" - including the modifiers they chose, and net of their discounts.
    lineUnitPrice() {
      if (!this.inCart || this.linePriceCents == null) return null
      return this.linePriceCents
    },

    // "from $X" only when a modifier can push the price up - otherwise the product always costs
    // exactly this, and the card renders a bare price. Owned here rather than by each adapter: the
    // two versions had drifted to different rules, with only V2 able to say "Free".
    priceLabel() {
      if (!this.product || !this.product.showPrices) return ''

      if (this.lineUnitPrice != null) {
        return this.lineUnitPrice === 0 ? 'Free' : formatPrice(this.lineUnitPrice, this.currency)
      }

      const from = fromPriceCents(this.product)
      if (from != null) return from > 0 ? `from ${formatPrice(from, this.currency)}` : ''
      if (this.product.hasModifiers) return this.product.basePriceCents > 0 ? this.formattedBasePrice : ''
      return this.product.basePriceCents === 0 ? 'Free' : this.formattedBasePrice
    },

    // A "was" price, against whichever number the card is actually showing: the line's, or the
    // product's own. An upsell price is only an offer next to the price it replaced - "$3.00" says
    // nothing, "$6.00 $3.00" is the pitch - which is why this survives the redesign.
    strikePriceLabel() {
      if (!this.product || !this.product.showPrices || !this.priceLabel) return ''

      const showing = this.lineUnitPrice != null ? this.lineUnitPrice : this.product.basePriceCents
      const before = this.lineUnitPrice != null ? this.lineOriginalPriceCents : this.basePriceOriginalCents
      if (before == null || showing == null || before <= showing) return ''

      return formatPrice(before, this.currency)
    }
  },

  methods: {
    open() {
      if (!this.interactive) return
      this.$emit('open')
    },

    remove() {
      this.$emit('remove')
    },

    decrease() {
      if (this.decreaseDisabled) return
      this.$emit('decrease')
    },

    increase() {
      if (this.increaseDisabled) return
      this.$emit('increase')
    },

    // The + before the product is in the cart: one more, for a caller with the stepper; the product
    // itself for any other.
    add() {
      if (this.cartStepper) this.increase()
      else this.open()
    }
  }
}
</script>

<style scoped>
.collapsed {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 0.5rem;
}
</style>
